import { apiClient, HEADER_TOKEN_TENANT } from "@/services/api"
import { logger } from "@/services/logger"
import router from "@/router"
import { defineStore } from "pinia"
import { useSettingsStore } from "@/store/settings"
import { useSubscriptionStore } from "@/store/subscription"
import { useRecentProjectsStore } from "@/store/recentProjects.js"
import { useTenantLogoStore } from "@/store/tenantLogo"
import { useReleaseNotesStore } from "@/store/releaseNotes"
import { useSettingsRoleStore } from "@/store/settings-role"
import { useCustomerContactPositionStore } from "@/store/customer-contactposition"
import { useSupplierKindStore } from "@/store/supplierKind"
import { useFileTagStore } from "@/store/fileTag"
import { useHourlyWageReportTagStore } from "@/store/hourlyWageReportTag"
import { useWorkTimeModelStore } from "@/store/worktimemodel"
import { useNotificationCenterStore } from "@/store/notificationCenter"
import { useDaySubmissionStore } from "@/store/daySubmission"
import { usePinStore } from "@/store/pins"
import { useFileBrowserStore } from "@/store/fileBrowser"
import * as Sentry from "@sentry/vue"
import { clearOwnDrafts, confirmLeave } from "@/services/unsavedChanges"

const TENANT_KEY = "TENANT"
const JWT_KEY = "token"
// Carries the address across the sign-out that a workspace change goes through, so the login form
// comes back filled in. sessionStorage rather than a query parameter: it dies with the tab and never
// reaches the browser history, and rather than the store, which is wiped by the sign-out itself.
const SWITCH_EMAIL_KEY = "switchWorkspaceEmail"

// Where to land after a successful login: back on the page an expired session interrupted (see the
// 401 handler in services/api.js), otherwise the Dashboard. Only in-app paths are accepted — "//host"
// would leave the app entirely.
const postLoginTarget = () => {
  const { redirect } = router.currentRoute.value.query
  const isInAppPath = typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//")
  return isInAppPath ? redirect : { name: "Dashboard" }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    id: null,
    email: "",
    firstName: "",
    lastName: "",
    permissions: [],
    // The workspace's company name, served by /principal. What the navigation shows as the signed-in
    // company — it used to show the workspace id, which was only ever there because the user had just
    // typed it into the login form.
    companyName: "",
    // Every workspace this person belongs to, for the switcher. Loaded after sign-in; a single entry
    // means there is nothing to switch between and the control stays hidden.
    workspaces: [],
    // Onboarding-tour state, tri-state on purpose: null = not loaded yet, false = still to be shown,
    // true = completed or dismissed. `login`/`register` fire loadPrincipal() without awaiting it, so a
    // plain `false` default would flash the tour before the server answers.
    onboardingCompleted: null,
    // The tenant's effective feature-sets, served by /principal rather than /billing/subscription:
    // the latter needs VIEW_BILLING, and while every predefined role is now seeded with it, roles are
    // per-tenant documents a customer can edit, so no permission is guaranteed to be there. Feature
    // gating has to work for everyone. See services/entitlements.js.
    entitlements: [],
    // The unreleased feature-sets, as /principal names them: granted workspaces only, not on sale. The backend's
    // setting is the only place this is decided, so a set's general release needs no change here. See
    // services/entitlements.js.
    unreleasedFeatureSets: [],
    // How far the workspace is with the supplier setup (`LEGACY`, `IN_PROGRESS`, `COMPLETED`); null until
    // /principal answers. See services/supplierMode.js.
    supplierSetup: null,
  }),
  getters: {
    getTenant: () => () => localStorage.getItem(TENANT_KEY),
    getId: state => state.id,
    getEmail: state => state.email,
    getFirstName: state => state.firstName,
    getLastName: state => state.lastName,
    getToken: () => () => localStorage.getItem(JWT_KEY),
    getCompanyName: state => state.companyName,
    getWorkspaces: state => state.workspaces,
    getPermissions: state => state.permissions,
    getOnboardingCompleted: state => state.onboardingCompleted,
    getEntitlements: state => state.entitlements,
    getUnreleasedFeatureSets: state => state.unreleasedFeatureSets,
    getSupplierSetup: state => state.supplierSetup,
  },
  actions: {
    setId(id) {
      this.id = id
    },
    setTenant(tenant) {
      localStorage.setItem(TENANT_KEY, tenant)
    },
    setEmail(email) {
      this.email = email
    },
    setFirstName(firstName) {
      this.firstName = firstName
    },
    setLastName(lastName) {
      this.lastName = lastName
    },
    setPermissions(permissions) {
      this.permissions = permissions
    },
    setToken(token) {
      localStorage.setItem(JWT_KEY, token.startsWith("Bearer ") ? token.replace("Bearer ", "") : token)
    },
    clearToken() {
      localStorage.setItem(JWT_KEY, "")
    },
    /**
     * Signs in with an email address and a password.
     *
     * The credentials identify a *person*, and a person can belong to several workspaces. When they
     * belong to exactly one the backend signs them straight into it. When they belong to several it
     * answers 200 with the list and no token — the credentials were accepted, what is missing is an
     * answer — and the caller submits again naming one via `tenantId`.
     *
     * @returns `{ workspaces }` when a choice is needed, `null` once signed in.
     */
    async login({ email, password, tenantId = null }) {
      // Nothing stale may survive a sign-in attempt: an old tenant would otherwise still satisfy
      // isLoggedIn() if the request failed halfway, and an old person's id would key the drafts
      // (services/unsavedChanges.ts) until the new principal arrives.
      this.setTenant("")
      this.clearToken()
      this.setId(null)

      // `tenant: false` is load-bearing — the interceptor would otherwise attach whatever workspace is
      // left in localStorage, and the backend reads an explicit header as the caller's choice. Somebody
      // whose last session was elsewhere would be sent back there, or bounced.
      const res = await apiClient.post(
        "/login",
        tenantId ? { email, password, tenantId } : { email, password },
        { headers: { tenant: false, authorization: false } }
      )

      const workspaces = res.data?.workspaces
      if (Array.isArray(workspaces) && workspaces.length) return { workspaces }

      const tenant = res.headers[HEADER_TOKEN_TENANT.toLowerCase()]
      if (!tenant) throw new Error("Login succeeded but the response carried no workspace.")

      await this.establishSession({ tenant, token: res.headers["authorization"], email })
      router.replace(postLoginTarget())
      return null
    },

    /**
     * Adopts a freshly issued session and loads everything that is scoped to its workspace.
     *
     * Shared by sign-in and by switching workspace, because those differ only in how the token was
     * obtained — afterwards the app has to forget the previous workspace's settings, subscription state
     * and recent projects just as completely either way.
     */
    async establishSession({ tenant, token, email = null }) {
      if (email !== null) this.setEmail(email)
      this.setFirstName("")
      this.setLastName("")
      this.setTenant(tenant)
      this.setToken(token)
      this.loadPrincipal()
      await useSettingsStore().loadSettings()
      // Load subscription state so the guard can route a locked/unsubscribed tenant to checkout.
      await useSubscriptionStore().loadStatus()
    },

    /** The workspaces this person belongs to. One entry means the switcher stays hidden. */
    loadWorkspaces() {
      return apiClient
        .get("/workspaces", { headers: { tenant: true, authorization: true } })
        .then(res => {
          this.workspaces = res.data?.workspaces ?? []
          return this.workspaces
        })
        .catch(error => {
          logger.error(`Failed to load workspaces: ${error}`)
          this.workspaces = []
          return []
        })
    },

    /**
     * Leaves the current workspace so that another one can be chosen.
     *
     * Which workspace you are in is settled at sign-in and then stays put for the session, so this is
     * a sign-out that says why: the login form comes back carrying the address, and the picker it
     * already shows to anyone with more than one membership does the actual choosing. Mid-session
     * swapping is deliberately not offered — every view reads workspace-scoped data once on mount, and
     * a fresh session is the only state the app is ever sure about.
     */
    async switchWorkspace() {
      // Asked here already, so a "keep editing" does not leave the address behind for a later sign-in.
      if (!(await confirmLeave())) return
      // Before the sign-out, not after: `logout` routes to the login form from its own `finally`, and
      // anything written afterwards would land behind the form that is meant to read it.
      if (this.email) sessionStorage.setItem(SWITCH_EMAIL_KEY, this.email)
      return this.logout({ reason: "switch_workspace" })
    },

    /** The address left behind by `switchWorkspace`, consumed once so a later sign-out starts blank. */
    takeSwitchWorkspaceEmail() {
      const email = sessionStorage.getItem(SWITCH_EMAIL_KEY)
      sessionStorage.removeItem(SWITCH_EMAIL_KEY)
      return email ?? ""
    },

    /**
     * @param reason why the session is ending, passed on to the login form when it is worth explaining
     *   (see `switchWorkspace`). A plain sign-out has nothing to say and sends none.
     */
    async logout({ reason = null } = {}) {
      // Signing out leaves the page like a navigation does, but the route guard cannot ask: by the time
      // it runs the session is gone, and it lets every navigation through. So the question comes here.
      if (!(await confirmLeave())) return
      // Signed out on purpose, perhaps on a device someone else picks up next. (An expired session keeps
      // its drafts: that is what they are for.)
      clearOwnDrafts({ tenant: this.getTenant(), userId: this.id })
      return apiClient.get("/logout", { headers: { tenant: true, authorization: true } }).finally(() => {
        this.setId(null)
        this.setEmail("")
        this.setFirstName("")
        this.setLastName("")
        this.setTenant("")
        this.setPermissions([])
        this.companyName = ""
        this.workspaces = []
        this.onboardingCompleted = null
        this.entitlements = []
        this.unreleasedFeatureSets = []
        this.supplierSetup = null
        this.clearToken()
        // Legacy: the login form used to remember the last workspace here. Nothing writes it any more;
        // removing it on the way out clears it from browsers that still carry one.
        localStorage.removeItem("tenant")
        this.clearSessionData()
        Sentry.setUser(null)
        Sentry.setTag("tenant", null)
        router.replace(reason ? { name: "Login", query: { reason } } : { name: "Login" })
      })
    },
    /**
     * Forgets what the stores cached for the workspace and the person that just left, so the next
     * sign-in (another workspace, or someone else at the same device) starts from nothing.
     *
     * Run by `logout` and by the 401 handler in services/api.js: an expired session is followed by the
     * same login form, and whoever signs in there must not see the previous workspace's data either.
     */
    clearSessionData() {
      useRecentProjectsStore().clearRecentProjects()
      useSubscriptionStore().$reset()
      // The next tenant to log in has its own logo (or none at all).
      useTenantLogoStore().reset()
      // The next person to sign in has their own seen state.
      useReleaseNotesStore().reset()
      // The bell keeps its last list until the first poll of the next session answers.
      useNotificationCenterStore().reset()
      // The Tagesmeldungen and the open count on the drawer were of the projects this person may see.
      useDaySubmissionStore().$reset()
      // Pins are the person's own.
      usePinStore().reset()
      // Workspace-scoped lookup lists that are cached for a whole session: roles, the contact
      // positions every contact id is resolved against, the supplier kinds, and the working-time
      // models (loaded once, among them the Standardmodell a new employee is prefilled with).
      useSettingsRoleStore().$reset()
      useCustomerContactPositionStore().reset()
      useSupplierKindStore().reset()
      useWorkTimeModelStore().$reset()
      // The file and report tags the tag pickers offer: they refetch on mount, but show what is
      // cached until the answer arrives.
      useFileTagStore().$reset()
      useHourlyWageReportTagStore().$reset()
      // The file browser's permissions and limits, fetched once per session: which types this person
      // may upload to and retag, and the workspace's due-date thresholds.
      useFileBrowserStore().clearSession()
    },
    /**
     * Creates a workspace and its first user. No workspace id is sent — the backend derives one from
     * the company name, and nobody ever has to type or remember it, because signing in resolves the
     * workspace from the email address.
     */
    register({ email, password, firstName, lastName, company }) {
      this.setToken("")
      this.clearToken()
      const recentProjectStore = useRecentProjectsStore()
      recentProjectStore.clearRecentProjects()

      return apiClient
        .post("/register", {
          tenant: {
            company: company,
          },
          user: {
            email,
            password,
            firstName,
            lastName,
          },
        })
        .then(res => {
          if (res.status === 201) {
            this.setEmail(res.data.email)
            this.setFirstName(res.data.firstName)
            this.setLastName(res.data.lastName)
            this.setTenant(res.data.tenant)
            this.loadPrincipal()
            this.setToken(res.data.token)
          }
          return res
        })
        .then(() => {
          const settingsStore = useSettingsStore()
          settingsStore.loadSettings().then(async () => {
            // New tenants must pay upfront: loadStatus returns an unsubscribed status and the
            // subscription guard sends them to Checkout when they land on the Dashboard.
            await useSubscriptionStore().loadStatus()
            router.replace({ name: "Dashboard" })
          })
        })
    },
    // Persists that the user has seen the onboarding tour. Optimistic on purpose: the local flag is
    // set first so the tour closes immediately and cannot re-open, and a failed request only costs one
    // extra tour next session — whereas waiting for the server risks showing it again on failure.
    completeOnboarding() {
      this.onboardingCompleted = true
      return (
        apiClient
          .put("/principal/onboarding", { completed: true }, { headers: { tenant: true, authorization: true } })
          // The server marks every release seen along with it (docs/release-notes.md). A changelog this
          // session already loaded — a replay from Help — would otherwise keep offering them as news.
          .then(() => {
            const releaseNotesStore = useReleaseNotesStore()
            if (releaseNotesStore.loaded) return releaseNotesStore.load()
          })
          .catch(error => logger.error(`Failed to persist onboarding completion: ${error}`))
      )
    },
    loadPrincipal() {
      return apiClient
        .get("/principal")
        .then(res => {
          this.setId(res.data.id)
          this.setPermissions(res.data.permissions)
          this.setEmail(res.data.email)
          this.setFirstName(res.data.firstName)
          this.setLastName(res.data.lastName)
          this.companyName = res.data.companyName ?? ""
          // `?? null` keeps the tri-state intact against an older backend that omits the field:
          // unknown must not read as "show the tour".
          this.onboardingCompleted = res.data.onboardingCompleted ?? null
          this.entitlements = res.data.entitlements ?? []
          this.unreleasedFeatureSets = res.data.unreleasedFeatureSets ?? []
          this.supplierSetup = res.data.supplierSetup ?? null
          // Fired alongside the principal rather than on demand: the navigation needs to know whether
          // there is more than one workspace before it can decide to render the switcher at all.
          this.loadWorkspaces()
          Sentry.setUser({
            id: res.data.id,
            email: res.data.email,
            username: `${res.data.firstName} ${res.data.lastName}`,
          })
          Sentry.setTag("tenant", this.getTenant())
        })
        .catch(error => {
          if (error.response?.status === 403) {
            const authStore = useAuthStore()
            const recentProjectStore = useRecentProjectsStore()
            authStore.setTenant("")
            authStore.clearToken()
            recentProjectStore.clearRecentProjects()
          }
        })
    },
  },
})
